// Shared helpers for the vehicle and IT ticket workflow modules.

// ─── ERRORS ──────────────────────────────────────────────────────────────────

// Maps an API error to form field errors, a stale-record refetch, or a toast.
export function classifyWorkflowError(error) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const code = data?.code || data?.detail?.code;

    if (status === 400) {
        const fieldErrors = {};
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            Object.entries(data).forEach(([field, value]) => {
                if (Array.isArray(value)) fieldErrors[field] = value.join(' ');
                else if (typeof value === 'string') fieldErrors[field] = value;
            });
        }
        return {
            kind: 'validation',
            fieldErrors,
            message: fieldErrors.detail || fieldErrors.non_field_errors || 'Please correct the highlighted fields.',
        };
    }

    if (status === 403) {
        return {
            kind: 'not_assignee',
            fieldErrors: {},
            message: code === 'not_assignee' || !data?.detail
                ? 'You are not named on this step. Someone may have acted before you.'
                : String(data.detail),
        };
    }

    if (status === 409) {
        return {
            kind: 'stale',
            fieldErrors: {},
            message: 'This record has already moved on. Refreshing to show its current state.',
        };
    }

    return {
        kind: 'other',
        fieldErrors: {},
        message: data?.detail || data?.message || error?.message || 'Something went wrong. Please try again.',
    };
}

// A 403 here means no module access — show a no-access panel, not an error.
export const isForbidden = (error) => error?.response?.status === 403;

// ─── PAYLOADS ────────────────────────────────────────────────────────────────

// Omits empty fields instead of sending null, which DRF often rejects.
export function withoutEmpty(obj) {
    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    );
}

// List endpoints may come back as a bare array or DRF-paginated.
export const toList = (data) => (Array.isArray(data) ? data : (data?.results || []));

// ─── FORMATTING ──────────────────────────────────────────────────────────────

export const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

// Times arrive as HH:MM:SS — the seconds are noise on screen.
export const formatTime = (value) => (value ? String(value).slice(0, 5) : '-');

// ─── TABLE THEME ─────────────────────────────────────────────────────────────

// Shared react-data-table theming, matching the travel and payroll tables.
export const dataTableStyles = {
    table: { style: { backgroundColor: 'var(--bg-dark)', color: 'var(--text-primary)' } },
    headRow: { style: { backgroundColor: 'var(--bg-light)', borderBottomColor: 'var(--border)', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' } },
    headCells: { style: { backgroundColor: 'transparent', color: 'var(--text-secondary)', paddingLeft: '16px', paddingRight: '16px' } },
    rows: { style: { backgroundColor: 'var(--bg-dark)', color: 'var(--text-primary)', borderBottomColor: 'var(--border)', fontSize: '13px' }, highlightOnHoverStyle: { backgroundColor: 'var(--bg-light)', color: 'var(--text-primary)', borderBottomColor: 'var(--border)', outline: 'none' } },
    cells: { style: { paddingLeft: '16px', paddingRight: '16px', color: 'var(--text-primary)' } },
    pagination: { style: { backgroundColor: 'var(--bg-dark)', color: 'var(--text-secondary)', borderTopColor: 'var(--border)' }, pageButtonsStyle: { color: 'var(--text-secondary)', fill: 'var(--text-secondary)', '&:hover:not(:disabled)': { backgroundColor: 'var(--bg-light)' }, '&:focus': { backgroundColor: 'var(--bg-light)' } } },
    subHeader: { style: { backgroundColor: 'var(--bg-dark)', paddingLeft: '0', paddingRight: '0' } },
    noData: { style: { backgroundColor: 'var(--bg-dark)', color: 'var(--text-secondary)' } },
};
